import { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import useDocumentTitle from "@/components/hooks/use-document-title";
import { usePathname } from "@/components/hooks/use-pathname";
import { AppNavbar } from "@/components/shared/app-navbar/index.web";
import { AuthProvider } from "@/components/shared/auth-provider";
import { ErrorBoundaryPage } from "@/components/shared/error-boundary-page";
import { LogoutDialog } from "@/components/shared/logout-dialog";
import { RouteFallback } from "@/components/shared/route-fallback";
import { PrivateRoute } from "@/components/shared/router/private-route";
import { Toaster } from "@/components/ui/toast/toaster";
import { ROUTES } from "@/constants/routes";
import { AuthContainer } from "@/containers/auth";
import { AccountGiftCardHistoryPage } from "@/containers/profile/account-gift-card/account-gift-card-history-page";
import { AccountGiftCard } from "@/containers/profile/account-gift-card/account-gift-card-main";
import { GiftCardNewPage } from "@/containers/profile/account-gift-card/account-gift-card-new-page";
import {
  GiftDetailPage,
  LegacyGiftDetailRedirect,
} from "@/containers/profile/account-gift-card/gift-detail-page";
import { ClientInstance } from "@/lib/graphql/apollo";
import { isChunkLoadError, reloadOnceForStaleChunk } from "@/lib/helpers/chunk-reload";
import { lazyNamed } from "@/lib/helpers/lazy";
import { posthogInstance } from "@/lib/monitoring/posthog";
import { setRoutePath } from "@/lib/monitoring/sentry";
import { getServiceLabel } from "@/lib/service/helpers";
import { type ServiceName } from "@/types/service";
import { ApolloProvider } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { PostHogProvider, type PostHog } from "posthog-js/react";

import { Toaster as LuceToaster } from "@luce/ui";

import { EventTracking } from "./pages/event-tracking";

// Pages
const HomepagePage = lazyNamed(() => import("@/app-web/pages/homepage"), "HomepagePage");
const ProfilePage = lazyNamed(() => import("@/app-web/pages/profile"), "ProfilePage");
const ServiceDetailPage = lazyNamed(
  () => import("@/app-web/pages/service-detail"),
  "ServiceDetailPage",
);
const SelectServicePage = lazyNamed(
  () => import("@/app-web/pages/select-service-page"),
  "SelectServicePage",
);
const NotFoundPage = lazyNamed(() => import("@/app-web/pages/not-found-page"), "NotFoundPage");
const VisitsPage = lazyNamed(() => import("./pages/visits"), "VisitsPage");
const RewardsPage = lazyNamed(() => import("./pages/rewards"), "RewardsPage");
const SubscriptionPage = lazyNamed(() => import("./pages/subscription"), "SubscriptionPage");
const PlusDashboardPage = lazyNamed(() => import("./pages/plus"), "PlusDashboardPage");
const RateVisit = lazyNamed(() => import("./pages/rate-visit"), "RateVisit");
const ManageVisit = lazyNamed(() => import("./pages/manage-visit"), "ManageVisit");
const RescheduleVisitPage = lazyNamed(
  () => import("./pages/reschedule-visit"),
  "RescheduleVisitPage",
);
const DownloadAppPage = lazyNamed(() => import("./pages/download-app-page"), "DownloadAppPage");
const CsatRatePage = lazyNamed(() => import("./pages/csat-rate-page"), "CsatRatePage");

// Notifications
const NotificationListPage = lazyNamed(
  () => import("./pages/notifications/list"),
  "NotificationListPage",
);
const NotificationDetailPage = lazyNamed(
  () => import("./pages/notifications/detail"),
  "NotificationDetailPage",
);

// Booking
const BookingPage = lazyNamed(() => import("@/app-web/pages/booking-page"), "BookingPage");
const BookingInfoPage = lazyNamed(() => import("./pages/booking-info-page"), "BookingInfoPage");
const BookingConfirmationPage = lazyNamed(
  () => import("./pages/booking-confirmation-page"),
  "BookingConfirmationPage",
);
const BookingCompletePage = lazyNamed(
  () => import("./pages/booking-complete-page"),
  "BookingCompletePage",
);
const SelectSlotPage = lazyNamed(() => import("./pages/select-slot-page"), "SelectSlotPage");

// Auth
const LoginPageWrapper = lazyNamed(
  () => import("@/containers/auth/login/login-page-wrapper"),
  "LoginPageWrapper",
);
const SignUpPageWrapper = lazyNamed(
  () => import("@/containers/auth/signup/sign-up-page-wrapper"),
  "SignUpPageWrapper",
);

// Profile children
const Overview = lazyNamed(() => import("@/containers/profile/overview"), "Overview");
const AccountInfo = lazyNamed(() => import("./pages/profile/account-info"), "AccountInfoPage");
const Payment = lazyNamed(() => import("@/containers/profile/payment"), "Payment");
const AddPaymentMethodPage = lazyNamed(
  () => import("@/app-web/pages/add-payment-method"),
  "AddPaymentMethodPage",
);
const NotificationSettingsPage = lazyNamed(
  () => import("./pages/profile/notification-settings"),
  "NotificationSettingsPage",
);
const ServiceProfileListPage = lazyNamed(
  () => import("./pages/profile/service-profile-list"),
  "ServiceProfileListPage",
);
const ServiceProfileFormContainer = lazyNamed(
  () => import("@/containers/profile/service-profile/service-profile-form"),
  "ServiceProfileFormContainer",
);
const MyVouchers = lazyNamed(() => import("@/containers/profile/my-vouchers"), "MyVouchers");
const Packages = lazyNamed(() => import("@/containers/profile/packages"), "Packages");
const PackageDetail = lazyNamed(
  () => import("./pages/profile/package-detail"),
  "PackageDetailPage",
);
const TerminatePackage = lazyNamed(
  () => import("@/containers/profile/packages/terminate-package"),
  "TerminatePackage",
);

// Profile addresses
const AddressesPage = lazyNamed(() => import("./pages/profile/addresses"), "AddressesPage");
const AddressFormPage = lazyNamed(() => import("@/app-web/pages/address-form"), "AddressFormPage");

// Profile contacts
const ContactsPage = lazyNamed(() => import("./pages/profile/contacts"), "ContactsPage");
const ContactFormPage = lazyNamed(() => import("@/app-web/pages/contact-form"), "ContactFormPage");

// Profile referral
const MyReferralPage = lazyNamed(() => import("./pages/profile/my-referral"), "MyReferralPage");
const AccountReferralHistory = lazyNamed(
  () => import("@/containers/profile/account-my-referral/account-my-referral-history-page"),
  "AccountMyReferralHistory",
);
const BusinessReferral = lazyNamed(
  () => import("@/containers/profile/business-referral"),
  "BusinessReferral",
);

// Profile invoices (heaviest — PDF routes)
const InvoiceList = lazyNamed(
  () => import("@/containers/profile/invoices/invoice-list"),
  "InvoiceList",
);
const InvoiceDetailPage = lazyNamed(
  () => import("./pages/profile/invoice-detail"),
  "InvoiceDetailPage",
);
const BillingPdfLayout = lazyNamed(
  () => import("./pages/profile/billing-pdf-layout"),
  "BillingPdfLayout",
);
const InvoicePdf = lazyNamed(() => import("@luce/customer-app"), "InvoicePdf");
const ServiceReportPdf = lazyNamed(() => import("@luce/customer-app"), "ServiceReportPdf");
const ReceiptPdf = lazyNamed(() => import("@luce/customer-app"), "ReceiptPdf");
const ReferralShareModal = lazyNamed(() => import("@luce/customer-app"), "ReferralShareModal");
const InvoiceAccountStatementPdfPage = lazyNamed(
  () => import("./pages/profile/invoice-account-statement-pdf"),
  "InvoiceAccountStatementPdfPage",
);

// Profile credit account
const CreditAccountOverview = lazyNamed(
  () => import("./pages/profile/credit-account-overview"),
  "CreditAccountOverviewPage",
);
const GeneralCreditPage = lazyNamed(
  () => import("./pages/profile/general-credit"),
  "GeneralCreditPage",
);
const DepartmentCreditPage = lazyNamed(
  () => import("./pages/profile/department-credit"),
  "DepartmentCreditPage",
);
const CreditTransactions = lazyNamed(() => import("@luce/customer-app"), "CreditTransactions");
const CreditAccountTransactionContainer = lazyNamed(
  () =>
    import("@/containers/profile/credit-account/credit-account-transaction/CreditAccountTransaction.container"),
  "CreditAccountTransactionContainer",
);

// Visits children
const VisitsUpcoming = lazyNamed(() => import("@/containers/visits/upcoming"), "VisitsUpcoming");
const VisitsHistory = lazyNamed(() => import("@/containers/visits/history"), "VisitsHistory");

// Rewards children
const PromosPage = lazyNamed(() => import("@/containers/rewards/promos"), "PromosPage");
const YourRewards = lazyNamed(() => import("@/containers/rewards/your-rewards"), "YourRewards");

// Plus children
const PlusPromosPage = lazyNamed(() => import("@/app-web/pages/plus/promos"), "PlusPromosPage");
const PlusHistoriesPage = lazyNamed(
  () => import("@/app-web/pages/plus/histories"),
  "PlusHistoriesPage",
);

export default function App() {
  return (
    <ErrorBoundary
      fallback={ErrorBoundaryPage}
      onError={(error) => {
        if (isChunkLoadError(error)) reloadOnceForStaleChunk();
      }}
    >
      {/* need to cast to unknown before Posthog, due to type referring to .native.ts */}
      <PostHogProvider client={posthogInstance as unknown as PostHog}>
        <ApolloProvider client={ClientInstance}>
          <AuthProvider>
            <BrowserRouter>
              <EventTracking />
              <DocumentTitle />
              <AppNavbar />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path={ROUTES.Root} element={<HomepagePage />} />
                  <Route path={ROUTES.Login} element={<LoginPageWrapper />} />
                  <Route path={ROUTES.Signup} element={<SignUpPageWrapper />} />
                  <Route
                    path={ROUTES.Profile.Root}
                    element={
                      <PrivateRoute>
                        <ProfilePage />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to={ROUTES.Profile.Children.Overview} replace />}
                    />
                    <Route path={ROUTES.Profile.Children.Overview} element={<Overview />} />
                    <Route path={ROUTES.Profile.Children.AccountInfo} element={<AccountInfo />} />
                    <Route path={ROUTES.Profile.Children.ServiceProfile.Root}>
                      <Route index element={<ServiceProfileListPage />} />
                      <Route
                        path={ROUTES.Profile.Children.ServiceProfile.Children.Type}
                        element={<ServiceProfileListPage />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.ServiceProfile.Children.edit}
                        element={<ServiceProfileFormContainer />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.ServiceProfile.Children.create}
                        element={<ServiceProfileFormContainer />}
                      />
                    </Route>
                    <Route
                      path={ROUTES.Profile.Children.NotificationSettings}
                      element={<NotificationSettingsPage />}
                    />
                    <Route path={ROUTES.Profile.Children.Invoices.Root}>
                      <Route
                        index
                        element={
                          <Navigate to={ROUTES.Profile.Children.Invoices.Children.Unpaid} replace />
                        }
                      />
                      <Route
                        path={ROUTES.Profile.Children.Invoices.Children.Unpaid}
                        element={<InvoiceList status="UNPAID" />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.Invoices.Children.Paid}
                        element={<InvoiceList status="PAID" />}
                      />
                    </Route>
                    <Route
                      path={ROUTES.Profile.Children.Invoices.Detail}
                      element={<InvoiceDetailPage />}
                    />
                    <Route
                      path={ROUTES.Profile.Children.Invoices.Detail}
                      element={<InvoiceDetailPage />}
                    />
                    <Route path={ROUTES.Profile.Children.Payment} element={<Payment />} />

                    <Route
                      path={ROUTES.Profile.Children.AddCreditCard}
                      element={<AddPaymentMethodPage />}
                    />

                    <Route path={ROUTES.Profile.Children.CreditAccount.Root}>
                      <Route index element={<CreditAccountOverview />} />
                      <Route
                        path={ROUTES.Profile.Children.CreditAccount.Children.General}
                        element={<GeneralCreditPage />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.CreditAccount.Children.GeneralTransactions}
                        element={<CreditTransactions />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.CreditAccount.Children.Massage}
                        element={<DepartmentCreditPage department="massage" />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.CreditAccount.Children.MassageTransactions}
                        element={<CreditTransactions department="massage" />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.CreditAccount.Children.Beauty}
                        element={<DepartmentCreditPage department="beauty" />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.CreditAccount.Children.BeautyTransactions}
                        element={<CreditTransactions department="beauty" />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.CreditAccount.Children.Pet}
                        element={<DepartmentCreditPage department="petCare" />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.CreditAccount.Children.PetTransactions}
                        element={<CreditTransactions department="petCare" />}
                      />
                    </Route>

                    <Route
                      path={ROUTES.Profile.Children.CreditAccountTransaction}
                      element={<CreditAccountTransactionContainer />}
                    />

                    <Route path={ROUTES.Profile.Children.MyVouchers} element={<MyVouchers />} />
                    {/* Packages page related */}
                    <Route path={ROUTES.Profile.Children.Packages} element={<Packages />} />

                    <Route path={ROUTES.Profile.Children.Package} element={<PackageDetail />}>
                      <Route path="terminate" element={<TerminatePackage />} />
                    </Route>
                    {/* Addresses page related */}
                    <Route path={ROUTES.Profile.Children.Addresses} element={<AddressesPage />} />
                    <Route path={ROUTES.Profile.Children.Address} element={<AddressFormPage />} />

                    <Route
                      path={ROUTES.Profile.Children.NewAddress}
                      element={<AddressFormPage />}
                    />

                    {/* Contacts page related */}
                    <Route path={ROUTES.Profile.Children.Contacts} element={<ContactsPage />} />

                    <Route path={ROUTES.Profile.Children.Contact} element={<ContactFormPage />} />

                    <Route
                      path={ROUTES.Profile.Children.NewContact}
                      element={<ContactFormPage />}
                    />

                    <Route
                      path={ROUTES.Profile.Children.AccountMyReferral}
                      element={<MyReferralPage />}
                    />

                    <>
                      <Route
                        path={ROUTES.Profile.Children.GiftCard}
                        element={<AccountGiftCard />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.GiftCardHistory}
                        element={<AccountGiftCardHistoryPage />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.GiftCardNew}
                        element={<GiftCardNewPage />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.GiftCardDetail}
                        element={<GiftDetailPage />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.GiftCardRedeemSuccess}
                        element={<LegacyGiftDetailRedirect />}
                      />
                      <Route
                        path={ROUTES.Profile.Children.GiftCardRedeemExpired}
                        element={<LegacyGiftDetailRedirect />}
                      />
                    </>

                    <Route
                      path={ROUTES.Profile.Children.AccountMyReferralHistory}
                      element={<AccountReferralHistory />}
                    />
                  </Route>

                  <Route
                    path={ROUTES.Profile.Root + "/" + ROUTES.Profile.Children.BusinessReferral}
                    element={
                      <PrivateRoute>
                        <BusinessReferral />
                      </PrivateRoute>
                    }
                  />

                  <Route element={<BillingPdfLayout />}>
                    <Route
                      path={ROUTES.Profile.Root + "/" + ROUTES.Profile.Children.Invoices.InvoicePdf}
                      element={<InvoicePdf />}
                    />
                    <Route
                      path={ROUTES.Profile.Root + "/" + ROUTES.Profile.Children.Invoices.ReceiptPdf}
                      element={<ReceiptPdf />}
                    />
                    <Route
                      path={ROUTES.Visits.Root + "/" + ROUTES.Visits.ServiceReportPdf}
                      element={<ServiceReportPdf />}
                    />
                  </Route>
                  <Route
                    path={
                      ROUTES.Profile.Root + "/" + ROUTES.Profile.Children.Invoices.AccountStatement
                    }
                    element={<InvoiceAccountStatementPdfPage />}
                  />

                  <Route
                    path={ROUTES.Visits.Root}
                    element={
                      <PrivateRoute>
                        <VisitsPage />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to={ROUTES.Visits.Children.Upcoming} replace />}
                    />
                    <Route path={ROUTES.Visits.Children.Upcoming} element={<VisitsUpcoming />} />
                    <Route path={ROUTES.Visits.Children.History} element={<VisitsHistory />} />
                  </Route>
                  <Route
                    path={ROUTES.Rewards.Root}
                    element={
                      <PrivateRoute>
                        <RewardsPage />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to={ROUTES.Rewards.Children.YourRewards} replace />}
                    />
                    <Route path={ROUTES.Rewards.Children.Promos} element={<PromosPage />} />
                    <Route path={ROUTES.Rewards.Children.YourRewards} element={<YourRewards />} />
                  </Route>
                  <Route
                    path={ROUTES.Subscription.Root}
                    element={
                      <PrivateRoute>
                        <ProfilePage />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<PlusDashboardPage />} />
                  </Route>
                  <Route
                    path={`${ROUTES.Subscription.Root}/${ROUTES.Subscription.Children.Subscription}`}
                    element={
                      <PrivateRoute>
                        <SubscriptionPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={`${ROUTES.Subscription.Root}/${ROUTES.Subscription.Children.Promos}`}
                    element={
                      <PrivateRoute>
                        <PlusPromosPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={`${ROUTES.Subscription.Root}/${ROUTES.Subscription.Children.Histories}`}
                    element={
                      <PrivateRoute>
                        <PlusHistoriesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route path={ROUTES.ServiceDetail} element={<ServiceDetailPage />} />
                  <Route path={ROUTES.SelectService} element={<SelectServicePage />} />
                  <Route path={ROUTES.Booking.Root} element={<BookingPage />} />
                  <Route path={ROUTES.Booking.SelectSlot} element={<SelectSlotPage />} />
                  <Route
                    path={ROUTES.Booking.BookingInfo}
                    element={
                      <PrivateRoute>
                        <BookingInfoPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.Booking.Confirmation}
                    element={
                      <PrivateRoute>
                        <BookingConfirmationPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.Booking.CompleteBooking}
                    element={
                      <PrivateRoute>
                        <BookingCompletePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.Notifications.Root}
                    element={
                      <PrivateRoute>
                        <NotificationListPage />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path={ROUTES.Notifications.Children.Detail}
                      element={<NotificationDetailPage />}
                    />
                  </Route>

                  <Route path={ROUTES.Visits.Rate} element={<RateVisit />} />
                  <Route path={ROUTES.Visits.ManageVisit} element={<ManageVisit />} />
                  <Route path={ROUTES.Visits.RescheduleVisit} element={<RescheduleVisitPage />} />
                  <Route path={ROUTES.Visits.AirconReschedule} element={<RescheduleVisitPage />} />
                  <Route path={ROUTES.DownloadApp} element={<DownloadAppPage />} />
                  <Route path={ROUTES.CsatRate} element={<CsatRatePage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
              <Toaster />
              <LuceToaster />
              <LogoutDialog />
              <AuthContainer />
              <Suspense fallback={null}>
                <ReferralShareModal />
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </ApolloProvider>
      </PostHogProvider>
    </ErrorBoundary>
  );
}

function DocumentTitle() {
  const pathName = usePathname();

  const [currentTitle, setTitle] = useState("Luce SG");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathName]);

  useEffect(() => {
    let title;
    const routeTitles: Record<string, string> = {
      "/": "Home - Luce SG",
      "/visits/upcoming": "Upcoming Visits - Luce SG",
      "/visits/history": "History - Luce SG",
      "/rewards/promos": "Promos - Luce SG",
      "/rewards/your-rewards": "Your Rewards - Luce SG",
      "/plus/promos": "Available Promo Code - Luce SG",
      "/plus/histories": "Recent Credit History - Luce SG",
    };
    if (pathName.startsWith("/booking/")) {
      const serviceName = pathName.split("/")[2];
      const serviceLabel = getServiceLabel(serviceName as ServiceName);
      if (serviceName === "pet-grooming") {
        title = `Book ${serviceLabel} - Luce SG`;
      } else {
        title = `Book ${serviceLabel} Service - Luce SG`;
      }
    } else if (pathName.startsWith("/service-detail/")) {
      const serviceName = pathName.split("/")[2];
      title = `Service ${getServiceLabel(serviceName as ServiceName)} - Luce SG`;
    } else if (pathName.startsWith("/profile")) {
      title = "Profile - Luce SG";
    } else {
      title = routeTitles[pathName] || "Luce SG";
    }
    setTitle(title);
    setRoutePath(pathName);
  }, [pathName]);

  useDocumentTitle(currentTitle);

  return null;
}
